import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getDailyStats, patchLeadAccountDailyStats, createLeadAccountDailyStats, getUsers } from '../api';
import { logError } from '../api';
import { isAdmin, isPropietario, isGestion, isEspectador, getUserId } from '../services/AuthService';
import useFilterPersistence from '../hooks/useFilterPersistence';


const DailyLeadStatsList = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dailyFilters, setDailyFilters] = useFilterPersistence('filters_daily_lead_stats', {
        selectedDate: new Date().toISOString().split('T')[0],
        selectedLeadId: ''
    });
    const { selectedDate, selectedLeadId } = dailyFilters;
    const setSelectedDate = (val) => setDailyFilters({ ...dailyFilters, selectedDate: val });
    const setSelectedLeadId = (val) => setDailyFilters({ ...dailyFilters, selectedLeadId: val });
    const [leadUsers, setLeadUsers] = useState([]);
    const [editedValues, setEditedValues] = useState({});
    const [savingIds, setSavingIds] = useState(new Set());
    const [editingCompletedIds, setEditingCompletedIds] = useState(new Set());
    
    const currentUserId = getUserId();
    const isSuperUser = isAdmin() || isPropietario() || isGestion() || isEspectador();

    useEffect(() => {
        loadLeadUsers();
    }, []);

    useEffect(() => {
        loadStats();
    }, [selectedDate, selectedLeadId]);

    const loadLeadUsers = async () => {
        try {
            const response = await getUsers(false);
            const leads = response.filter(user => 
                !user.disabled && user.roles && user.roles.some(role => role.name === 'Leads')
            );
            setLeadUsers(leads);
            
            if (!isSuperUser) {
                setSelectedLeadId(currentUserId || '');
            }
        } catch (error) {
            console.error('Error loading lead users:', error);
        }
    };

    const loadStats = useCallback(async () => {
        setLoading(true);
        try {
            const leadId = isSuperUser ? selectedLeadId || null : currentUserId;
            const response = await getDailyStats(selectedDate, leadId);
            // Crear un ID único para cada estadística si no existe
            const statsWithUniqueIds = (response || []).map((stat, index) => ({
                ...stat,
                uniqueId: stat.id || `temp-${stat.leadAccount?.id || index}-${index}`
            }));
            setStats(statsWithUniqueIds);
        } catch (error) {
            logError(error);
            console.error('Error loading daily stats:', error);
        } finally {
            setLoading(false);
        }
    }, [selectedDate, selectedLeadId, isSuperUser, currentUserId]);

    const pendingStats = stats.filter(stat => stat.pendingToInput);
    const completedStats = stats.filter(stat => !stat.pendingToInput);
    const isToday = selectedDate === new Date().toISOString().split('T')[0];

    const handleInputChange = (statUniqueId, newValue) => {
        // Permitir valores vacíos durante la edición, pero convertir a 0 al guardar
        setEditedValues(prev => ({
            ...prev,
            [statUniqueId]: newValue
        }));
    };

    const handleSave = async (stat) => {
        const editedValue = editedValues[stat.uniqueId];
        const currentValue = editedValue !== undefined ? editedValue : (stat.leadCount || 0);
        
        // Convertir valor vacío o inválido a 0
        const numericValue = currentValue === '' || currentValue === null || currentValue === undefined ? 0 : parseInt(currentValue) || 0;
        
        setSavingIds(prev => new Set([...prev, stat.uniqueId]));
        try {
            if (stat.pendingToInput) {
                // Crear nuevo registro para pendientes de entrada
                const createData = {
                    leadAccountId: stat.leadAccount.id,
                    statDate: selectedDate,
                    leadCount: numericValue
                };
                
                // Si no es superuser, autoasignar el usuario actual como lead
                if (!isSuperUser) {
                    createData.assignedLeadId = currentUserId;
                }
                
                await createLeadAccountDailyStats(createData);
            } else {
                // Usar patch para actualizar solo la cantidad de leads en completadas
                await patchLeadAccountDailyStats(stat.id, { leadCount: numericValue });
            }
            
            setEditedValues(prev => {
                const updated = { ...prev };
                delete updated[stat.uniqueId];
                return updated;
            });
            
            // Limpiar estado de edición si es una estadística completada
            if (!stat.pendingToInput) {
                setEditingCompletedIds(prev => {
                    const updated = new Set(prev);
                    updated.delete(stat.uniqueId);
                    return updated;
                });
            }
            
            loadStats();
        } catch (error) {
            logError(error);
            console.error('Error saving lead count:', error);
        } finally {
            setSavingIds(prev => {
                const updated = new Set(prev);
                updated.delete(stat.uniqueId);
                return updated;
            });
        }
    };

    const getCurrentValue = (stat) => {
        const editedValue = editedValues[stat.uniqueId];
        if (editedValue !== undefined) {
            return editedValue;
        }
        return stat.leadCount !== null && stat.leadCount !== undefined ? stat.leadCount : 0;
    };

    const hasChanges = (stat) => {
        return editedValues[stat.uniqueId] !== undefined;
    };

    const toggleEditCompleted = (stat) => {
        setEditingCompletedIds(prev => {
            const updated = new Set(prev);
            if (updated.has(stat.uniqueId)) {
                updated.delete(stat.uniqueId);
                // Limpiar valor editado si se cancela
                setEditedValues(prevEdited => {
                    const updatedEdited = { ...prevEdited };
                    delete updatedEdited[stat.uniqueId];
                    return updatedEdited;
                });
            } else {
                updated.add(stat.uniqueId);
            }
            return updated;
        });
    };

    return (
        <Container fluid className="my-4" data-testid="daily-lead-stats-container">
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <h1 data-testid="page-title">Estadísticas Diarias de Leads</h1>
                        <Button 
                            variant="outline-primary" 
                            onClick={() => navigate('/lead-account-history')}
                            data-testid="view-history-button"
                        >
                            Ver Histórico
                        </Button>
                    </div>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col md={4}>
                    <Form.Group>
                        <Form.Label>Fecha</Form.Label>
                        <Form.Control
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => e.target.showPicker && e.target.showPicker()}
                            data-testid="date-picker"
                        />
                    </Form.Group>
                </Col>
                {isSuperUser && (
                    <Col md={4}>
                        <Form.Group>
                            <Form.Label>Lead Asignado</Form.Label>
                            <Form.Select
                                value={selectedLeadId}
                                onChange={(e) => setSelectedLeadId(e.target.value)}
                                data-testid="lead-selector"
                            >
                                <option value="">Todos</option>
                                {leadUsers.map(user => (
                                    <option key={user.id} value={user.id}>
                                        {user.username}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                )}
            </Row>

            {loading ? (
                <p data-testid="loading-message">Cargando...</p>
            ) : (
                <>
                    {pendingStats.length > 0 && (
                        <Row className="mb-4">
                            <Col>
                                <h3 data-testid="pending-stats-title">Pendientes de Entrada</h3>
                                <div className="table-responsive">
                                    <table className="table table-striped table-bordered" data-testid="pending-stats-table">
                                        <thead>
                                            <tr>
                                                <th className="text-center">Cuenta de Lead</th>
                                                {isSuperUser && <th className="text-center">Lead Asignado</th>}
                                                <th className="text-center">CP</th>
                                                <th className="text-center">Correo</th>
                                                <th className="text-center">Fuente del Lead</th>
                                                <th className="text-center">Cantidad de Leads</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pendingStats.map((stat, index) => (
                                                <tr key={stat.uniqueId} data-testid={`pending-stat-row-${stat.uniqueId}`}>
                                                    <td>{stat.leadAccount?.name || ''}</td>
                                                    {isSuperUser && <td>{stat.leadAccount?.assignedLead?.username || ''}</td>}
                                                    <td>{stat.leadAccount?.zipCode || ''}</td>
                                                    <td>{stat.leadAccount?.email || ''}</td>
                                                    <td>{stat.leadAccount?.leadSource?.name || ''}</td>
                                                    <td className="text-center">
                                                        {(isSuperUser || isToday) ? (
                                                            <div className="d-flex align-items-center justify-content-center gap-2">
                                                                <input
                                                                    type="number"
                                                                    value={getCurrentValue(stat)}
                                                                    onChange={(e) => handleInputChange(stat.uniqueId, e.target.value)}
                                                                    min="0"
                                                                    className="form-control form-control-sm"
                                                                    style={{ width: '80px' }}
                                                                    placeholder="0"
                                                                    data-testid={`pending-lead-count-input-${stat.uniqueId}`}
                                                                />
                                                                <button
                                                                    className="btn btn-sm btn-success"
                                                                    onClick={() => handleSave(stat)}
                                                                    disabled={savingIds.has(stat.uniqueId)}
                                                                    data-testid={`pending-save-button-${stat.uniqueId}`}
                                                                >
                                                                    {savingIds.has(stat.uniqueId) ? 'Guardando...' : 'Guardar'}
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            stat.leadCount
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Col>
                        </Row>
                    )}
                    
                    {completedStats.length > 0 && (
                        <Row>
                            <Col>
                                <h3 data-testid="completed-stats-title">Completadas</h3>
                                <div className="table-responsive">
                                    <table className="table table-striped table-bordered" data-testid="completed-stats-table">
                                        <thead>
                                            <tr>
                                                <th className="text-center">Cuenta de Lead</th>
                                                {isSuperUser && <th className="text-center">Lead Asignado</th>}
                                                <th className="text-center">CP</th>
                                                <th className="text-center">Correo</th>
                                                <th className="text-center">Fuente del Lead</th>
                                                <th className="text-center">Cantidad de Leads</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {completedStats.map((stat, index) => (
                                                <tr key={stat.uniqueId} data-testid={`completed-stat-row-${stat.uniqueId}`}>
                                                    <td className="text-center">{stat.leadAccount?.name || ''}</td>
                                                    {isSuperUser && <td className="text-center">{stat.leadAccount?.assignedLead?.username || ''}</td>}
                                                    <td className="text-center">{stat.leadAccount?.zipCode || ''}</td>
                                                    <td className="text-center">{stat.leadAccount?.email || ''}</td>
                                                    <td className="text-center">{stat.leadAccount?.leadSource?.name || ''}</td>
                                                    <td className="text-center">
                                                        {editingCompletedIds.has(stat.uniqueId) ? (
                                                            <div className="d-flex align-items-center justify-content-center gap-2">
                                                                <input
                                                                    type="number"
                                                                    value={getCurrentValue(stat)}
                                                                    onChange={(e) => handleInputChange(stat.uniqueId, e.target.value)}
                                                                    min="0"
                                                                    className="form-control form-control-sm"
                                                                    style={{ width: '80px' }}
                                                                    placeholder="0"
                                                                    data-testid={`completed-lead-count-input-${stat.uniqueId}`}
                                                                />
                                                                <button
                                                                    className="btn btn-sm btn-success"
                                                                    onClick={() => handleSave(stat)}
                                                                    disabled={savingIds.has(stat.uniqueId)}
                                                                    data-testid={`completed-save-button-${stat.uniqueId}`}
                                                                >
                                                                    {savingIds.has(stat.uniqueId) ? 'Guardando...' : 'Guardar'}
                                                                </button>
                                                                <button
                                                                    className="btn btn-sm btn-secondary"
                                                                    onClick={() => toggleEditCompleted(stat)}
                                                                    disabled={savingIds.has(stat.uniqueId)}
                                                                    data-testid={`completed-cancel-button-${stat.uniqueId}`}
                                                                >
                                                                    Cancelar
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <div className="d-flex align-items-center justify-content-center gap-2">
                                                                <span>{stat.leadCount}</span>
                                                                {(isSuperUser || isToday) && (
                                                                    <button
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        onClick={() => toggleEditCompleted(stat)}
                                                                        title="Editar"
                                                                        data-testid={`completed-edit-button-${stat.uniqueId}`}
                                                                    >
                                                                        ✏️
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Col>
                        </Row>
                    )}
                    
                    {stats.length === 0 && (
                        <Row>
                            <Col>
                                <p className="text-center" data-testid="no-stats-message">No se encontraron estadísticas para la fecha seleccionada.</p>
                            </Col>
                        </Row>
                    )}
                </>
            )}
        </Container>
    );
};

export default DailyLeadStatsList;